import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Stack,
  Title,
  Text,
  Button,
  Alert,
  Anchor,
  Loader,
  Center,
} from '@mantine/core';
import { IconAlertCircle, IconCheck, IconMail } from '@tabler/icons-react';
import { useAuthService } from '@actschurch/ccms-sdk';
import { markEmailConfirmedLocally } from '@/utils/emailConfirmationMarker';

export default function ConfirmEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const authService = useAuthService();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [confirming, setConfirming] = useState(!!token);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    if (!token || !email) return;
    let cancelled = false;
    (async () => {
      try {
        await authService.confirmEmail({ email, token });
        if (!cancelled) {
          // Stamp a local marker so the banner stops showing for this email
          // straight away. The cached JWT still carries EmailConfirmed=false
          // until the user re-signs-in; the marker bridges that gap.
          markEmailConfirmedLocally(email);
          setConfirmed(true);
          setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
        }
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'This confirmation link is invalid or has expired.');
      } finally {
        if (!cancelled) setConfirming(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, email, authService, navigate]);

  const onResend = async () => {
    setResending(true);
    setError(null);
    try {
      await authService.resendConfirmation({
        email,
        appUrl: window.location.origin,
      });
      setResent(true);
    } catch (err: any) {
      setError(err?.message || 'Could not resend the confirmation link.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Container size={460} my={60}>
      <Title order={2} ta="center" mb="md">
        Confirm your email
      </Title>

      <Paper withBorder shadow="sm" p="lg" radius="md">
        {confirming ? (
          <Center py="lg"><Loader /></Center>
        ) : confirmed ? (
          <Stack>
            <Alert icon={<IconCheck size={16} />} color="green" variant="light">
              Email confirmed. Redirecting to sign-in…
            </Alert>
            <Anchor component={Link} to="/auth/login" ta="center">Sign in now</Anchor>
          </Stack>
        ) : token ? (
          <Stack>
            <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
              {error || 'This confirmation link is invalid or has expired.'}
            </Alert>
            {email && (
              <>
                <Text size="sm">Need a fresh link?</Text>
                <Button
                  onClick={onResend}
                  loading={resending}
                  leftSection={<IconMail size={16} />}
                  variant="light"
                >
                  Resend confirmation email
                </Button>
                {resent && (
                  <Alert color="green" variant="light">
                    A new confirmation link has been sent to <strong>{email}</strong>.
                  </Alert>
                )}
              </>
            )}
          </Stack>
        ) : (
          <Stack>
            <Text>
              We've sent a confirmation link to{' '}
              <strong>{email || 'your email address'}</strong>. Click it to activate
              your account before signing in.
            </Text>
            <Text size="sm" c="dimmed">
              If you don't see it within a minute, check your spam folder or request a new one.
            </Text>
            {email && (
              <Button
                onClick={onResend}
                loading={resending}
                leftSection={<IconMail size={16} />}
                variant="light"
              >
                Resend confirmation email
              </Button>
            )}
            {resent && (
              <Alert color="green" variant="light">
                A new confirmation link has been sent.
              </Alert>
            )}
            {error && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
            <Anchor component={Link} to="/auth/login" ta="center" size="sm">
              Back to sign in
            </Anchor>
          </Stack>
        )}
      </Paper>
    </Container>
  );
}
